import { Controller, useForm } from 'react-hook-form';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import { Title, Description } from '@radix-ui/react-dialog';
import React, { useState, useContext } from 'react';

import { Button, Dialog, DialogCloseButton } from '$partials/ui';
import { Checkbox, PhoneInput } from '$partials/fields';
import { getGlobalQuery } from '$utilities/global-data';
import { PropertyContext } from '$contexts/property';
import { SimpleImage } from '$partials/media';
import { useAnalytics } from '$contexts/analytics';
import { useRecaptcha } from '$hooks/use-recaptcha';
import IconChevron from '$partials/icons/icon-chevron';
import IconClose from '$partials/icons/icon-close';

/**
 * @param {Array<Array<any>>} arrays
 * @returns {Array<any>}
 */
const findFirstArrayWithItems = (arrays) => {
    return arrays.find((array) => (Array.isArray(array) && array.length > 0));
};

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/partials/properties').TeamMemberEnquireDialog}
 */
const TeamMemberEnquireDialog = ({
    agent: agentProp,
    closeDialog,
    externalId: externalIdProp,
    isOpen,
    office: officeProp,
    propertyReferenceId: propertyReferenceIdProp,
}) => {
    const propertyContext = useContext(PropertyContext);
    const { getTrackingData, createSourceSummary } = useAnalytics();
    const { getToken: getRecaptchaToken } = useRecaptcha();

    const { enquiryForm } = getGlobalQuery();

    const agent = agentProp || propertyContext?.property?.primaryAgent;
    const disclaimerText = enquiryForm?.disclaimerText;
    const externalId = externalIdProp || propertyContext?.property?.externalId;
    const office = officeProp || propertyContext?.property?.office;
    const propertyReferenceId = propertyReferenceIdProp || propertyContext?.property?.propertyReferenceId;
    const pageTitle = propertyContext?.property?.pageTitle;
    const pageUrl = propertyContext?.property?.pageUrl;
    const whatsappMessage = `I am interested in: ${[pageTitle, pageUrl].filter((value) => Boolean(value)).join(' ')}`.trim();

    const displayEmailAddresses = findFirstArrayWithItems([
        office?.newHomesDisplayEmailAddresses,
        office?.lettingsDisplayEmailAddresses,
        office?.salesDisplayEmailAddresses,
        office?.generalDisplayEmailAddresses
    ]);

    const displayPhoneNumbers = findFirstArrayWithItems([
        office?.newHomesDisplayPhoneNumbers,
        office?.lettingsDisplayPhoneNumbers,
        office?.salesDisplayPhoneNumbers,
        office?.generalDisplayPhoneNumbers
    ]);

    const displayWhatsapp = findFirstArrayWithItems([
        office?.newHomesDisplayWhatsapp,
        office?.lettingsDisplayWhatsapp,
        office?.salesDisplayWhatsapp,
        office?.generalDisplayWhatsapp
    ]);

    const { control, register, handleSubmit, formState: { errors, isDirty }, watch } = useForm();

    const bodyValue = watch('body');
    const firstNameValue = watch('firstName');
    const lastNameValue = watch('lastName');
    const emailValue = watch('email');
    const [formError, setFormError] = useState();
    const [completed, setCompleted] = useState(false);
    const [bodyFocused, setBodyFocused] = useState(false);
    const [firstNameFocused, setFirstNameFocused] = useState(false);
    const [lastNameFocused, setLastNameFocused] = useState(false);
    const [emailFocused, setEmailFocused] = useState(false);

    const handleSubmission = async (formData) => {
        setFormError(null);
        try {

            const trackingData = getTrackingData();
            const recaptchaToken = await getRecaptchaToken('property_enquiry');

            const response = await fetch('/api/v1/property-enquiry', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify({
                    body: formData.body,
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    listingId: externalId,
                    newsletter: formData.newsletter,
                    phone: formData.phone,
                    recaptchaToken,
                    utm: createSourceSummary(trackingData),
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data?.message);
            } else {
                setCompleted(true);
            }
        } catch (err) {
            setFormError(err?.message ?? err);
        }
    };

    return (
        <Dialog
            className='theme-light enquire-dialog'
            onOpenChange={(open) => {
                if (!open) { closeDialog(); }
            }}
            open={isOpen}
            warnBeforeClose={isDirty ? 'Are you sure you want to close this dialog?' : undefined}>
            {/* Modal Panel */}
            <div className='p-16 sm:px-32 sm:py-60 text-charcoal theme-light'>
                <DialogCloseButton className='absolute z-20 top-24 right-24 flex select-none h-[40px] w-[40px] md:h-[44px] md:w-[44px] rounded justify-center items-center button-secondary-filled'>
                    <IconClose />
                </DialogCloseButton>

                {!completed && (
                    <>
                        {/* Agent Info */}
                        {(agent || office || displayPhoneNumbers) && (
                            <div className='bg-main-background'>
                                <div className='flex flex-col md:flex-row gap-24'>
                                    {agent?.profileImage && (
                                        <SimpleImage
                                            {...agent.profileImage}
                                            className='object-cover w-[147px] h-[147px] md:w-[250px] md:h-[250px] shrink-0'/>
                                    )}

                                    <div className='w-full'>
                                        <Title className='type-style-3 sm:type-style-4 mb-8'>
                                            {agent?.name}
                                        </Title>
                                        <Description className='sr-only'>
                                            Complete the form to make an enquiry
                                        </Description>

                                        {(agent?.position || office?.title) && (
                                            <p className='type-style-eyebrow mb-8'>
                                                {[agent?.position, office?.title].filter((t) => Boolean(t)).join(' / ')}
                                            </p>
                                        )}

                                        {propertyReferenceId && (
                                            <p className='type-style-eyebrow mb-8'>
                                                {`Property Ref: ${propertyReferenceId}`}
                                            </p>
                                        )}

                                        <div className='type-style-eyebrow mt-24 w-full'>
                                            {displayPhoneNumbers?.map((phone, i) => {
                                                return (
                                                    <div
                                                        className='border-t border-main-border py-16'
                                                        key={i}>
                                                        <a
                                                            className='flex items-center justify-between w-full'
                                                            href={`tel:${phone.replace(/[^\+\d]/g, '')}`}>
                                                            <span>
                                                                {`Tel: ${phone}`}
                                                            </span>
                                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                                        </a>
                                                    </div>
                                                );
                                            })}

                                            {displayWhatsapp?.map((whatsapp, i) => {
                                                return (
                                                    <div
                                                        className='border-t border-main-border py-16'
                                                        key={i}>
                                                        <a
                                                            className='flex items-center justify-between w-full'
                                                            href={`https://wa.me/${whatsapp.replace(/[^\+\d]/g, '')}${pageTitle && pageUrl ? `?text=${encodeURIComponent(whatsappMessage)}` : ''}`}>
                                                            <span>
                                                                {`Whatsapp: ${whatsapp}`}
                                                            </span>
                                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                                        </a>
                                                    </div>
                                                );
                                            })}

                                            {displayEmailAddresses?.map((email, i) => {
                                                return (
                                                    <div
                                                        className='border-t border-main-border py-16'
                                                        key={i}>
                                                        <a
                                                            className='flex items-center justify-between w-full'
                                                            href={`mailto:${email}`}>
                                                            <span>
                                                                {`Email: ${email}`}
                                                            </span>
                                                            <IconChevron className='inline-block ml-5.5 -rotate-90' />
                                                        </a>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Contact Form */}
                        <div className={`flex flex-col ${agent ? 'pt-56' : 'pt-0'}`}>
                            <form
                                className='flex flex-col gap-18'
                                data-gtm-form='listing_enquiry_form'
                                onSubmit={handleSubmit(handleSubmission)}>
                                {/* Form Sections */}
                                <div className='flex flex-col gap-32'>
                                    {/* Your Details Section */}
                                    <div className='flex flex-col gap-8'>
                                        <p className='type-style-form-body-r text-main-text-secondary'>
                                            {'Your Details'}
                                        </p>

                                        {/* First Row: First Name + Last Name */}
                                        <div className='flex flex-col sm:flex-row gap-8'>
                                            <div className='flex-1'>
                                                <div className='relative'>
                                                    <label
                                                        className={`absolute left-12 top-1/2 -translate-y-1/2 transition-all pointer-events-none text-input-label ${firstNameFocused || firstNameValue ? 'top-10 -translate-y-0 type-style-input-label' : 'type-style-eyebrow'}`}
                                                        htmlFor='firstName'>
                                                        {'First name*'}
                                                    </label>
                                                    <input
                                                        id='firstName'
                                                        type='text'
                                                        {...register('firstName', { required: 'First name is required' })}
                                                        className={`w-full h-[44px] px-12 input-background border border-input-border rounded text-input-label type-style-typed-input ${firstNameFocused || firstNameValue ? 'pt-[22px] pb-[6px]' : 'py-10'}`}
                                                        onBlur={() => setFirstNameFocused(false)}
                                                        onFocus={() => setFirstNameFocused(true)} />
                                                </div>

                                                {errors.firstName && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.firstName.message)}
                                                    </p>
                                                )}
                                            </div>

                                            <div className='flex-1'>
                                                <div className='relative'>
                                                    <label
                                                        className={`absolute left-12 top-1/2 -translate-y-1/2 transition-all pointer-events-none text-input-label ${lastNameFocused || lastNameValue ? 'top-10 -translate-y-0 type-style-input-label' : 'type-style-eyebrow'}`}
                                                        htmlFor='lastName'>
                                                        {'Last name*'}
                                                    </label>
                                                    <input
                                                        id='lastName'
                                                        type='text'
                                                        {...register('lastName', { required: 'Last name is required' })}
                                                        className={`w-full h-[44px] px-12 input-background border border-input-border rounded text-input-label type-style-typed-input ${lastNameFocused || lastNameValue ? 'pt-[22px] pb-[6px]' : 'py-10'}`}
                                                        onBlur={() => setLastNameFocused(false)}
                                                        onFocus={() => setLastNameFocused(true)} />
                                                </div>

                                                {errors.lastName && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.lastName.message)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        {/* Second Row: Email + Phone */}
                                        <div className='flex flex-col sm:flex-row gap-8'>
                                            <div className='flex-1'>
                                                <div className='relative'>
                                                    <label
                                                        className={`absolute left-12 top-1/2 -translate-y-1/2 transition-all pointer-events-none text-input-label ${emailFocused || emailValue ? 'top-10 -translate-y-0 type-style-input-label' : 'type-style-eyebrow'}`}
                                                        htmlFor='email'>
                                                        {'Email*'}
                                                    </label>
                                                    <input
                                                        id='email'
                                                        type='email'
                                                        {...register('email', {
                                                            required: 'Email is required',
                                                            pattern: {
                                                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                                message: 'Invalid email address'
                                                            }
                                                        })}
                                                        className={`w-full h-[44px] px-12 input-background border border-input-border rounded text-input-label type-style-typed-input ${emailFocused || emailValue ? 'pt-[22px] pb-[6px]' : 'py-10'}`}
                                                        onBlur={() => setEmailFocused(false)}
                                                        onFocus={() => setEmailFocused(true)} />
                                                </div>

                                                {errors.email && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.email.message)}
                                                    </p>
                                                )}
                                            </div>

                                            <div className='flex-1 relative phone-input'>
                                                <label
                                                    className='sr-only'
                                                    htmlFor='Phone'>
                                                    {'Phone*'}
                                                </label>
                                                <PhoneInput
                                                    control={control}
                                                    id='Phone'
                                                    name='phone'
                                                    placeholder='Phone number'
                                                    rules={{
                                                        required: 'Phone number is required',
                                                        validate: (value) => {
                                                            return !isPossiblePhoneNumber(value) ? 'Enter a valid phone number' : null;
                                                        }
                                                    }} />
                                                {errors.phone && (
                                                    <p className='text-system-fail text-sm mt-4'>
                                                        {String(errors.phone.message)}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Your Enquiry Section */}
                                    <div className='flex flex-col gap-8'>
                                        <p className='type-style-form-body-r text-main-text-secondary'>
                                            {'Your Enquiry'}
                                        </p>

                                        <div className='relative'>
                                            <label
                                                className={`absolute left-12 transition-all pointer-events-none type-style-eyebrow text-input-label ${bodyFocused || bodyValue ? 'top-10 type-style-input-label' : 'top-10'}`}
                                                htmlFor='enquiry'>
                                                {'Message'}
                                            </label>
                                            <textarea
                                                id='enquiry'
                                                {...register('body', { required: 'Please enter your enquiry' })}
                                                className={`w-full h-[151px] px-12 rounded input-background border border-input-border text-input-label type-style-typed-input resize-none ${
                                                    bodyFocused || bodyValue ? 'pt-[28px] pb-10' : 'py-10'
                                                }`}
                                                onBlur={() => setBodyFocused(false)}
                                                onFocus={() => setBodyFocused(true)}>
                                            </textarea>
                                        </div>
                                        {errors.body && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.body.message)}
                                            </p>
                                        )}

                                        <div className='flex gap-12 items-center'>
                                            <Controller
                                                control={control}
                                                name='termsAccepted'
                                                render={({ field: { onChange, value, name } }) => {
                                                    return (
                                                        <Checkbox
                                                            checked={value}
                                                            id='termsAccepted'
                                                            name={name}
                                                            onCheckedChange={onChange} />
                                                    );
                                                }}
                                                rules={{
                                                    required: 'You must accept the terms and conditions'
                                                }} />
                                            <label
                                                className='type-style-caption text-main-text-secondary'
                                                htmlFor='termsAccepted'>
                                                {'I agree to Terms of Use'}
                                            </label>
                                        </div>
                                        {errors.termsAccepted && (
                                            <p className='text-system-fail text-sm mt-4'>
                                                {String(errors.termsAccepted.message)}
                                            </p>
                                        )}

                                        <div className='flex gap-12 items-center'>
                                            <Controller
                                                control={control}
                                                name='newsletter'
                                                render={({ field: { onChange, value, name } }) => {
                                                    return (
                                                        <Checkbox
                                                            checked={value}
                                                            id='newsletter'
                                                            name={name}
                                                            onCheckedChange={onChange} />
                                                    );
                                                }} />
                                            <label
                                                className='type-style-caption text-main-text-secondary'
                                                htmlFor='newsletter'>
                                                {'I would like to receive the Beauchamp Estates newsletter'}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Button Row with Disclaimer */}
                                <div className='flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-y-12 pt-20 sm:pt-12'>
                                    {disclaimerText && (
                                        <div
                                            className='disclaimer-rich-text type-style-caption text-main-text-secondary max-w-[365px]'
                                            dangerouslySetInnerHTML={{ __html: disclaimerText }} />
                                    )}
                                    <Button
                                        className='button-brand-filled w-[82px] py-13 px-16 rounded type-style-eyebrow'
                                        type='submit'>
                                        {'Submit'}
                                    </Button>
                                </div>
                                {formError && (
                                    <p
                                        className='text-system-fail text-sm mt-4'
                                        role='alert'>
                                        {formError}
                                    </p>
                                )}
                            </form>
                        </div>
                    </>
                )}
                {/* Completed screen */}
                {completed && (
                    <div
                        className='h-full flex flex-col justify-center sm:justify-normal'
                        data-gtm-success='listing_enquiry_form_success'>
                        <div>
                            <p className='type-style-3 mb-12'>
                                {'Thank You for Your Enquiry'}
                            </p>
                            <p className='type-style-body-r mb-16'>
                                {'A dedicated team member will get back to you soon'}
                            </p>
                            <Button
                                className='flex select-none h-[40px] md:h-[44px] px-16 rounded justify-center items-center button-secondary-filled'
                                onClick={closeDialog}>
                                {'Close'}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default TeamMemberEnquireDialog;
